import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: 'Which three pillars shape religio-traditio-cultural well-being?',
    options: ['Religion, Culture, Tradition', 'Faith, Family, Community', 'Prayer, Ritual, Heritage', 'Belief, Value, Custom', 'Spirituality, Identity, Resilience'],
    correctAnswer: 0,
    explanation: 'Religion, Culture, and Tradition are the three pillars.'
  },
  {
    question: 'How does spirituality differ from religiosity?',
    options: ['Spirituality is personal, religiosity is institutional', 'They are the same', 'Spirituality is about rituals', 'Religiosity is about values', 'Spirituality is only for elders'],
    correctAnswer: 0,
    explanation: 'Spirituality is a personal experience; religiosity is more about organized practices.'
  },
  {
    question: 'What is a key Filipino anchor for mental stability?',
    options: ['Faith and family traditions', 'Modern technology', 'Individualism', 'Political beliefs', 'Economic status'],
    correctAnswer: 0,
    explanation: 'Faith and family traditions are powerful anchors for Filipino mental and emotional stability.'
  },
  {
    question: 'Cultural values act as a:',
    options: ['Compass for well-being', 'Barrier to growth', 'Source of conflict', 'Temporary trend', 'Financial asset'],
    correctAnswer: 0,
    explanation: 'Cultural values guide well-being like a compass.'
  },
  {
    question: 'Reflection: What helps you feel grounded during stressful times?',
    options: ['Family/cultural traditions', 'Ignoring stress', 'Material possessions', 'Social media', 'None of the above'],
    correctAnswer: 0,
    explanation: 'Family or cultural traditions help you feel grounded.'
  }
];

const ReligioTraditioCulturalModule1QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('rtcM1');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  React.useEffect(() => {
    if (responses[currentQuestion] !== undefined) {
      setSelectedAnswer(responses[currentQuestion]);
      setShowExplanation(true);
    } else {
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  }, [currentQuestion, responses]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowExplanation(true);
    setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  if (isLoading) {
    return <div className="module-form"><p>Loading quiz...</p></div>;
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">Congratulations!</h2>
        <p className="completion-subtitle">You've completed the Module 1 quiz!</p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">Key Achievements</h3>
            <ul className="achievement-list">
              <li><span className="check"></span>Understanding the roots of self</li>
              <li><span className="check"></span>Faith, culture, and tradition as anchors</li>
              <li><span className="check"></span>Reflection on belonging</li>
            </ul>
          </div>
          <button className="resource-button" onClick={() => navigate('/panel/learn/religio-traditio-cultural-resources')}>Return to Resources</button>
        </div>
      </div>
    );
  }

  const currentQuizQuestion = quizQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left religio-traditional-cultural-panel">
          <div className="module-header">
            <h1>Module 1 Quiz</h1>
            <p className="module-subtitle">Foundations of Religio-Traditio-Cultural Well-Being</p>
            <hr className="module-divider" />
            <p className="module-tool-info">Lecture + reflection + journaling + community sharing</p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">Question {currentQuestion + 1} of {quizQuestions.length}</span>
            </div>
            <div className="question-section">
              <h2 className="question-title">{currentQuizQuestion.question}</h2>
              <div className="answer-options">
                {currentQuizQuestion.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuizQuestion.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuizQuestion.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button className="btn-secondary" onClick={currentQuestion === 0 ? () => navigate('/panel/learn/religio-traditio-cultural-resources') : handleBack}>
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button className="btn-primary" onClick={handleNext} disabled={responses[currentQuestion] === undefined}>Next Question</button>
              ) : (
                <button className="btn-primary" onClick={handleNext} disabled={responses[currentQuestion] === undefined}>Complete Quiz</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReligioTraditioCulturalModule1QuizPage;


